import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../shared/components/pagination-bar/pagination-bar.component';
import {
  AuditAction,
  AuditLogEntry,
  AuditLogService,
  AuditTargetType,
} from '../services/audit-log.service';
import { Company, CompanyService } from '../services/company.service';
import { CompanyContextService } from '../services/company-context.service';

/**
 * Employee update audit log, in two modes.
 *
 * Under a company route the company comes from CompanyContextService and the
 * company filter is hidden. At /admin/audit-log it runs cross-company and offers
 * a company dropdown — previously this was a free-text box requiring a pasted UUID.
 */
@Component({
  selector: 'app-admin-audit-log',
  standalone: true,
  imports: [CommonModule, FormsModule, TitleBarComponent, PaginationBarComponent],
  templateUrl: './admin-audit-log.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-audit-log.component.scss'],
})
export class AdminAuditLogComponent implements OnInit, OnDestroy {
  private readonly auditLogService = inject(AuditLogService);
  private readonly companyService = inject(CompanyService);
  private readonly companyContext = inject(CompanyContextService);

  entries: AuditLogEntry[] = [];
  loading = true;
  errorMessage = '';

  /** Set when rendered inside the company detail shell. */
  scopedCompany: Company | null = null;
  /** Cross-company mode only: the dropdown selection. */
  companyFilterId = '';
  companyOptions: Company[] = [];

  searchTerm = '';
  targetType: AuditTargetType | '' = '';
  action: AuditAction | '' = '';
  dateFrom = '';
  dateTo = '';

  readonly targetTypeOptions: AuditTargetType[] = ['admin', 'attendant', 'staff'];
  readonly actionOptions: AuditAction[] = [
    'create',
    'update',
    'delete',
    'deactivate',
    'role_change',
  ];

  currentPage = 1;
  itemsPerPage = 25;
  totalItems = 0;
  totalPages = 0;

  expandedRowId: string | null = null;

  private readonly search$ = new Subject<string>();
  private searchSub?: Subscription;
  private contextSub?: Subscription;
  private companiesSub?: Subscription;
  private requestSub?: Subscription;

  get isScoped(): boolean {
    return !!this.scopedCompany;
  }

  private get effectiveCompanyId(): string | undefined {
    const id = this.scopedCompany?.id || this.companyFilterId;
    return id.trim() || undefined;
  }

  ngOnInit(): void {
    this.contextSub = this.companyContext.current$.subscribe(company => {
      const changed = (company?.id || '') !== (this.scopedCompany?.id || '');
      this.scopedCompany = company;
      if (!company && !this.companyOptions.length) {
        this.loadCompanyOptions();
      }
      if (changed) {
        this.currentPage = 1;
        this.loadEntries();
      }
    });

    this.searchSub = this.search$.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => {
      this.currentPage = 1;
      this.loadEntries();
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.contextSub?.unsubscribe();
    this.companiesSub?.unsubscribe();
    this.requestSub?.unsubscribe();
  }

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  onSearchInput(value: string): void {
    this.searchTerm = value;
    this.search$.next(value.trim());
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadEntries();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadEntries();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;
    this.currentPage = 1;
    this.loadEntries();
  }

  toggleExpanded(entry: AuditLogEntry): void {
    this.expandedRowId = this.expandedRowId === entry.id ? null : entry.id;
  }

  private loadCompanyOptions(): void {
    this.companiesSub?.unsubscribe();
    this.companiesSub = this.companyService
      .listCompaniesPaginated({ page: 1, limit: 500 })
      .subscribe({
        next: res => {
          this.companyOptions = res.status && res.data ? res.data.companies || [] : [];
        },
        error: () => {
          this.companyOptions = [];
        },
      });
  }

  loadEntries(): void {
    this.requestSub?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';

    this.requestSub = this.auditLogService
      .listAuditLog({
        page: this.currentPage,
        limit: this.itemsPerPage,
        company_id: this.effectiveCompanyId,
        target_type: this.targetType || undefined,
        action: this.action || undefined,
        date_from: this.dateFrom || undefined,
        date_to: this.dateTo || undefined,
        search: this.searchTerm.trim() || undefined,
      })
      .subscribe({
        next: res => {
          this.loading = false;
          if (res.status && res.data) {
            this.entries = res.data.entries || [];
            this.totalItems = res.data.totalItems || 0;
            this.currentPage = res.data.currentPage || this.currentPage;
            this.totalPages = res.data.totalPages || 0;
            this.itemsPerPage = res.data.itemsPerPage || this.itemsPerPage;
            return;
          }
          this.entries = [];
          this.totalItems = 0;
          this.totalPages = 0;
          this.errorMessage = res.message || 'Unable to load audit log.';
        },
        error: err => {
          this.loading = false;
          this.entries = [];
          this.totalItems = 0;
          this.totalPages = 0;
          this.errorMessage = err?.error?.message || err?.message || 'Failed to load audit log.';
        },
      });
  }

  formatCreatedAt(value?: string): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleString();
  }
}
